import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Icon,
  Modal,
  Form,
  Input,
  Message,
  Segment,
  Header,
  Label,
  Checkbox,
} from "semantic-ui-react";
import { StrategicAxis } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { toast } from "react-toastify";
import "./StrategicAxesConfig.scss";

const strategicAxisController = new StrategicAxis();

const DEFAULT_AXES = [
  { name: "Agua", description: "Gestión del recurso hídrico", order: 1 },
  { name: "Energía/GEI", description: "Energía y gases de efecto invernadero", order: 2 },
  { name: "Residuos", description: "Gestión de residuos", order: 3 },
  { name: "Químicos/ZDHC", description: "Gestión de químicos y ZDHC", order: 4 },
  { name: "OHS/Seguridad", description: "Salud y seguridad ocupacional", order: 5 },
  { name: "Cumplimiento/Legal", description: "Cumplimiento legal y normativo", order: 6 },
  { name: "Comunidad/Biodiversidad", description: "Comunidad y biodiversidad", order: 7 },
];

export function StrategicAxesConfig({ site }) {
  const { accessToken } = useAuth();
  const [axes, setAxes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingAxis, setEditingAxis] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    order: 1,
    active: true,
  });

  useEffect(() => {
    if (site && accessToken) {
      loadAxes();
    }
  }, [site, accessToken]);

  const loadAxes = async () => {
    setLoading(true);
    try {
      const response = await strategicAxisController.getAxesBySite(accessToken, site, false);
      //console.log(response)
      if (response.code === 200 && response.axes) {
        setAxes(response.axes.sort((a, b) => (a.order || 0) - (b.order || 0)));
      }
    } catch (error) {
      console.error("Error al cargar ejes:", error);
      toast.error("Error al cargar ejes estratégicos");
    } finally {
      setLoading(false);
    }
  };

  const handleInitializeDefaults = async () => {
    if (!window.confirm("¿Desea inicializar los ejes estratégicos por defecto? Esto no eliminará los existentes.")) {
      return;
    }
    setLoading(true);
    try {
      const response = await strategicAxisController.initializeDefaultAxes(accessToken, site);
      if (response.code === 201) {
        toast.success("Ejes por defecto inicializados");
        await loadAxes();
      }
    } catch (error) {
      console.error("Error al inicializar ejes:", error);
      toast.error(error.message || "Error al inicializar ejes");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAxis = async () => {
    if (!formData.name || !formData.name.trim()) {
      toast.error("El nombre del eje es requerido");
      return;
    }

    setLoading(true);
    try {
      if (editingAxis) {
        // Actualizar
        const response = await strategicAxisController.updateAxis(
          accessToken,
          editingAxis._id,
          formData
        );
        if (response.code === 200) {
          toast.success("Eje actualizado");
          await loadAxes();
        }
      } else {
        // Crear
        const response = await strategicAxisController.createAxis(accessToken, {
          ...formData,
          site,
        });
        if (response.code === 201) {
          toast.success("Eje creado");
          await loadAxes();
        }
      }
      setModalOpen(false);
      setFormData({ name: "", description: "", order: 1, active: true });
      setEditingAxis(null);
    } catch (error) {
      console.error("Error al guardar eje:", error);
      toast.error(error.message || "Error al guardar eje");
    } finally {
      setLoading(false);
    }
  };

  const handleEditAxis = (axis) => {
    setEditingAxis(axis);
    setFormData({
      name: axis.name,
      description: axis.description || "",
      order: axis.order || 1,
      active: axis.active !== undefined ? axis.active : true,
    });
    setModalOpen(true);
  };

  const handleDeleteAxis = async (axisId) => {
    if (!window.confirm("¿Está seguro de desactivar este eje estratégico?")) {
      return;
    }
    setLoading(true);
    try {
      const response = await strategicAxisController.deleteAxis(accessToken, axisId);
      if (response.code === 200) {
        toast.success("Eje desactivado");
        await loadAxes();
      }
    } catch (error) {
      console.error("Error al desactivar eje:", error);
      toast.error("Error al desactivar eje");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (axis) => {
    setLoading(true);
    try {
      const response = await strategicAxisController.updateAxis(accessToken, axis._id, {
        active: !axis.active,
      });
      if (response.code === 200) {
        toast.success(axis.active ? "Eje desactivado" : "Eje activado");
        await loadAxes();
      }
    } catch (error) {
      console.error("Error al cambiar estado:", error);
      toast.error("Error al cambiar estado del eje");
    } finally {
      setLoading(false);
    }
  };

  if (!site) {
    return (
      <Message warning>
        <Message.Header>Seleccione un sitio</Message.Header>
        <p>Debe seleccionar un sitio para gestionar los ejes estratégicos.</p>
      </Message>
    );
  }

  return (
    <div className="strategic-axes-config">
      <Segment>
        <Header as="h3">
          <Icon name="sitemap" />
          Configuración de Ejes Estratégicos
        </Header>

        <Message info>
          <Message.Header>¿Qué son los ejes estratégicos?</Message.Header>
          <p>
            Los ejes estratégicos son las áreas temáticas principales sobre las que se definen los
            objetivos ambientales y de sostenibilidad. Estos ejes se utilizan para organizar y
            categorizar los objetivos en la matriz.
          </p>
        </Message>

        <div style={{ marginBottom: "15px" }}>
          <Button
            primary
            icon
            labelPosition="left"
            onClick={() => {
              setEditingAxis(null);
              setFormData({ name: "", description: "", order: axes.length + 1, active: true });
              setModalOpen(true);
            }}
          >
            <Icon name="plus" />
            Agregar Eje
          </Button>
          <Button
            color="blue"
            icon
            labelPosition="left"
            onClick={handleInitializeDefaults}
            disabled={loading}
          >
            <Icon name="download" />
            Inicializar Ejes por Defecto
          </Button>
          <Button icon labelPosition="left" onClick={loadAxes} disabled={loading}>
            <Icon name="refresh" />
            Recargar
          </Button>
        </div>

        <Table celled striped>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell width={1}>#</Table.HeaderCell>
              <Table.HeaderCell width={4}>Nombre</Table.HeaderCell>
              <Table.HeaderCell width={7}>Descripción</Table.HeaderCell>
              <Table.HeaderCell width={2} textAlign="center">
                Estado
              </Table.HeaderCell>
              <Table.HeaderCell width={2} textAlign="center">
                Acciones
              </Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {loading && (
              <Table.Row>
                <Table.Cell colSpan="5" textAlign="center">
                  <Icon loading name="spinner" />
                  Cargando...
                </Table.Cell>
              </Table.Row>
            )}
            {!loading && axes.length === 0 && (
              <Table.Row>
                <Table.Cell colSpan="5" textAlign="center">
                  <Message info>
                    <Message.Header>No hay ejes configurados</Message.Header>
                    <p>
                      Use el botón "Inicializar Ejes por Defecto" para cargar los ejes estándar o
                      "Agregar Eje" para crear uno personalizado.
                    </p>
                  </Message>
                </Table.Cell>
              </Table.Row>
            )}
            {!loading &&
              axes.map((axis, index) => (
                <Table.Row key={axis._id} positive={axis.active} negative={!axis.active}>
                  <Table.Cell>{axis.order || index + 1}</Table.Cell>
                  <Table.Cell>
                    <strong>{axis.name}</strong>
                  </Table.Cell>
                  <Table.Cell>{axis.description || "-"}</Table.Cell>
                  <Table.Cell textAlign="center">
                    <Checkbox
                      toggle
                      checked={axis.active}
                      onChange={() => handleToggleActive(axis)}
                    />
                  </Table.Cell>
                  <Table.Cell textAlign="center">
                    <Button icon size="mini" onClick={() => handleEditAxis(axis)}>
                      <Icon name="edit" />
                    </Button>
                    <Button
                      icon
                      size="mini"
                      color="red"
                      onClick={() => handleDeleteAxis(axis._id)}
                      disabled={!axis.active}
                    >
                      <Icon name="trash" />
                    </Button>
                  </Table.Cell>
                </Table.Row>
              ))}
          </Table.Body>
        </Table>

        {axes.length > 0 && (
          <Message>
            <p>
              <strong>Total de ejes:</strong> {axes.length} |{" "}
              <strong>Activos:</strong> {axes.filter((a) => a.active).length} |{" "}
              <strong>Inactivos:</strong> {axes.filter((a) => !a.active).length}
            </p>
          </Message>
        )}
      </Segment>

      {/* Modal para agregar/editar eje */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)} size="small">
        <Modal.Header>{editingAxis ? "Editar Eje Estratégico" : "Agregar Eje Estratégico"}</Modal.Header>
        <Modal.Content>
          <Form loading={loading}>
            <Form.Field required>
              <label>Nombre del Eje</label>
              <Input
                placeholder="ej: Agua, Energía/GEI, Residuos..."
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </Form.Field>

            <Form.TextArea
              label="Descripción"
              placeholder="Descripción del eje estratégico..."
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />

            <Form.Field>
              <label>Orden</label>
              <Input
                type="number"
                min="1"
                value={formData.order}
                onChange={(e) => setFormData({ ...formData, order: Number(e.target.value) })}
              />
            </Form.Field>

            <Form.Field>
              <Checkbox
                label="Activo"
                checked={formData.active}
                onChange={(e, { checked }) => setFormData({ ...formData, active: checked })}
              />
            </Form.Field>
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setModalOpen(false)}>Cancelar</Button>
          <Button primary onClick={handleSaveAxis}>
            Guardar
          </Button>
        </Modal.Actions>
      </Modal>
    </div>
  );
}
